/* eslint-disable camelcase */
import { Injectable, Injector } from '@angular/core';
import { FeatureFlag } from '@app/enums/feature-flag.enum';
import { KYBSigningOfficerRequiredError } from '@app/errors/kyb/kyb-signing-officer-required.error';
import { ReverificationPreviousInquiryRequiredError } from '@app/errors/kyb/reverification-previous-inquiry-required-error';
import { Office } from '@app/models/company/office.model';
import { Employee } from '@app/models/employee/employee.model';
import { PersonaAccount } from '@app/models/kyc-kyb/persona-account.model';
import { PersonaInquiryFields } from '@app/models/kyc-kyb/persona-inquiry-fields.model';
import { PersonaInquiryToken } from '@app/models/kyc-kyb/persona-inquiry-token.model';
import { PersonaInquiry } from '@app/models/kyc-kyb/persona-inquiry.model';
import { PersonaResendInvite } from '@app/models/kyc-kyb/persona-resend-invite.model';
import {
    HumiInquiryStatus,
    INCOMPLETE_HUMI_STATUSES,
    INVALIDATE_CACHE_STATUSES,
    KYB_PREFILL_FIELDS,
    KYBPrefill,
    KYC_KYB_RE_VERIFICATION_DEADLINE_DAYS,
    KYC_KYB_RE_VERIFICATION_MINIMUM_DEADLINE,
    KYCPrefill,
    NewPersonaSessionAttributes,
    ResumePersonaSessionAttributes,
} from '@app/models/kyc-kyb/types';
import { PersonaScope } from '@app/modules/payroll/components/kyc-kyb/types';
import { PayrollResources } from '@app/modules/payroll/payroll.resources';
import { OnboardingStatusService } from '@app/modules/self-serve/services/onboarding-status.service';
import { environment } from '@env/environment';
import * as Sentry from '@sentry/angular';
import { getYear } from 'date-fns';
import { isNil } from 'lodash-es';
import { Client as PersonaClient } from 'persona';
import { InquiryError } from 'persona/dist/lib/interfaces';
import { BehaviorSubject, combineLatest, defer, from, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { CountryService } from '../country.service';
import { FeatureService } from '../feature.service';

/**
 * Service for interacting with Persona as part of the Know-Your-Client and Know-Your-Business verification flows
 */
@Injectable({
    providedIn: 'root',
})
export class KYCKYBService {
    /**
     * The Persona Account (KYC)
     */
    readonly personaAccount: Observable<PersonaAccount>;

    /**
     * The Persona Company (KYB)
     */
    readonly personaCompany: Observable<PersonaAccount>;

    /**
     * KYC Status
     */
    get accountStatus(): Observable<HumiInquiryStatus> {
        if (this.isKYCComplete) {
            return of('complete');
        }
        return this.personaAccount.pipe(map((personaAccount) => personaAccount.status));
    }

    /**
     * KYB Status
     */
    get companyStatus(): Observable<HumiInquiryStatus> {
        return this.personaCompany.pipe(
            map((personaCompany) => {
                return personaCompany.status;
            })
        );
    }

    /**
     * Gets the KYB Inquiry details including all child UBO inquiries
     */
    get companyInquiry(): Observable<PersonaInquiry | undefined> {
        return this.personaCompany.pipe(
            switchMap((personaCompany) => {
                // Need an inquiry ID to send to the API
                if (!personaCompany.inquiry?.inquiryId) {
                    return of(undefined);
                }
                return PersonaInquiry.param('inquiry', personaCompany.inquiry.inquiryId).show();
            })
        );
    }

    get isKYCComplete(): boolean {
        return !!this.authService.account.personaCompletedAt;
    }

    /**
     * The Persona Client can take some time to load the script. This allows us to show a spinner in the interim
     */
    get isLoading(): boolean {
        return this._isLoading;
    }

    private _personaAccount = new BehaviorSubject<PersonaAccount | undefined>(undefined);
    private _personaCompany = new BehaviorSubject<PersonaAccount | undefined>(undefined);
    private _primaryOffice = new ReplaySubject<Office>(1);
    /**
     * Defer and injector are used to ensure that we're only retrieving Countries when it's required.
     * The CountryService makes the API request the moment it's instantiated, by using defer and injector, it will only instantiate upon being subscribed to
     */
    private _countries = defer(() => this.injector.get(CountryService).allCountries);
    /**
     * We only need to retrieve the list of countries once. This flag allows us to know that the countries is already populated
     */
    private _hasCountries = false;
    private _personaClient?: PersonaClient;
    private _isLoading = false;

    /**
     * Fields that SHOULD be pre-filled from previous inquiry during re-verification
     * All KYB prefill fields EXCEPT 'initiator_employee_id' (which is always set to the current user)
     */
    private readonly REVERIFICATION_INCLUDED_FIELDS = KYB_PREFILL_FIELDS.filter(
        (field) => field !== 'initiator_employee_id'
    ) as readonly string[];

    constructor(
        private authService: AuthService,
        private injector: Injector,
        private featureService: FeatureService
    ) {
        // Initializes both Observables (Note they won't be actually executed until subscribed to)
        this.personaAccount = this.getPersonaAccount('KYC');
        this.personaCompany = this.getPersonaAccount('KYB');
    }

    private get accountId(): number {
        return this.authService.accountId;
    }

    private get companyId(): number {
        return this.authService.company.id;
    }

    /**
     * Gets the KYC status of an employee OTHER than the currently logged in user.
     * Unlike the current user's status, this will never be cached
     */
    getAccountStatusForEmployee(employee: Employee): Observable<HumiInquiryStatus> {
        if (employee.account.personaCompletedAt) {
            return of('complete');
        }
        return from(this.getAccountModel('KYC', employee.account.id.toString())).pipe(
            map((personaAccount) => personaAccount.status)
        );
    }

    /**
     * Public function for launching the Persona iFrame
     * Will handle both new and existing sessions, as well as KYC or KYB
     */
    launchPersona(scope: PersonaScope, prefillKYBFields?: KYBPrefill): Observable<void> {
        this._isLoading = true;
        const personaAccount = scope === 'KYB' ? this.personaCompany : this.personaAccount;

        // Populate Country Codes (this is run only once KYB or KYC is launched)
        if (!this._hasCountries) {
            this._countries.pipe(take(1)).subscribe(() => (this._hasCountries = true));
        }

        return personaAccount.pipe(
            take(1), // PersonaAccount is occasionally refreshed - we only need the latest value from it to launch Persona
            switchMap(({ templateId, accountId, environmentId, inquiry, status }) => {
                // For KYB flow: handle both regular and re-verification
                if (scope === 'KYB') {
                    return from(this.shouldShowReVerificationContent()).pipe(
                        switchMap((isReVerification) => {
                            // 1. Check if we should resume an existing inquiry (works for both re-verification and regular)
                            // Resume any inquiry that exists and is not declined or approved
                            const canResume =
                                inquiry?.inquiryId &&
                                inquiry.inquiryId.trim() !== '' &&
                                inquiry.status !== 'declined' &&
                                inquiry.status !== 'approved';

                            if (canResume) {
                                return this.createSessionToken(inquiry.inquiryId);
                            }

                            // 2. Not resuming - need signing officer details to create new inquiry
                            if (isNil(prefillKYBFields)) {
                                this._isLoading = false;
                                throw new KYBSigningOfficerRequiredError();
                            }

                            // 3. Create new inquiry with or without re-verification data
                            if (isReVerification) {
                                // Fetch and merge re-verification prefill data
                                return from(this.getReVerificationPrefillData()).pipe(
                                    map((reVerificationData) => ({
                                        templateId,
                                        referenceId: accountId,
                                        environmentId,
                                        prefillFields: reVerificationData
                                            ? { ...reVerificationData, ...prefillKYBFields }
                                            : prefillKYBFields,
                                    }))
                                );
                            }

                            // Create regular KYB inquiry
                            return of({
                                templateId,
                                referenceId: accountId,
                                environmentId,
                                prefillFields: prefillKYBFields,
                            });
                        })
                    );
                }

                // For KYC: Resume existing inquiry or create new
                if (inquiry?.inquiryId && inquiry.status !== 'declined' && status === 'not_complete') {
                    return this.createSessionToken(inquiry.inquiryId);
                }

                return of({ templateId, referenceId: accountId, environmentId, prefillFields: prefillKYBFields });
            }),
            tap(() => {
                // Populate the office data for KYB that is used for pre-filling the form fields
                if (scope === 'KYB') {
                    const { company } = this.authService;
                    // Populate Office Data (this is run every time KYB is launched in case the data has changed)
                    Office.param('company', company.id)
                        .find(company.primaryAddressId)
                        // This is saved in a private variable to be consumed later
                        .then((office) => this._primaryOffice.next(office));
                }
            }),
            tap((personaAttributes) => {
                const { prefillFields, ...attributes } = personaAttributes as any;
                this.openPersonaClient(attributes, scope, prefillFields);
            }),
            catchError((err) => {
                this._isLoading = false;
                throw err;
            }),
            map(() => void 0) // We don't need to return any data so just map to void
        );
    }

    /**
     * This function is run once per render of a view that shows the status of a user's KYC/KYB
     * The purpose is to check if they have a status that is pending action from a 3rd-party (ie. "completed" needs an admin to "approve")
     *
     * If the user's inquiry IS in one of these statuses then we clear the cached inquiry so that we can live retrieve it once more from the BE
     */
    checkForRefresh(scope: PersonaScope): void {
        (scope === 'KYB' ? this._personaCompany : this._personaAccount)
            .pipe(
                take(1),
                filter(
                    (personaAccount) =>
                        !!personaAccount?.inquiry?.status &&
                        INVALIDATE_CACHE_STATUSES.includes(personaAccount.inquiry.status)
                )
            )
            .subscribe(this.refreshPersona.bind(this, scope));
    }

    /**
     * This function is run once per render of a view that shows the status of a user's KYC/KYB
     * The purpose is to check if they have a status that is pending action from a 3rd-party (ie. "completed" needs an admin to "approve")
     *
     * If the user's inquiry IS in one of these statuses then we clear the cached inquiry so that we can live retrieve it once more from the BE
     */
    isIncompleteState(status: HumiInquiryStatus): boolean {
        return INCOMPLETE_HUMI_STATUSES.includes(status);
    }

    /**
     * Re-sends an invite to a user for the KYB Persona workflow
     */
    resendInvite({ inquiryId }: PersonaInquiry): Observable<void> {
        return from(new PersonaResendInvite({ inquiryId }).create()).pipe(map(() => void 0));
    }

    /**
     * Fetches inquiry fields from the backend for a given inquiry ID
     * Used for pre-filling re-verification inquiries
     */
    private fetchInquiryFields(inquiryId: string): Promise<Record<string, any>> {
        return PersonaInquiryFields.param('inquiry_id', inquiryId)
            .show()
            .then((response) => response.data || {});
    }

    /**
     * Gets pre-fill data for re-verification:
     * - If current inquiry is 'approved', uses its fields
     * - If current inquiry is NOT 'approved', uses fields from last_approved_inquiry (if available)
     * Only runs if Re-Verification is true
     */
    private async getReVerificationPrefillData(): Promise<KYBPrefill | undefined> {
        try {
            // Get the current inquiry ID and status from personaCompany
            const personaCompany = await this.personaCompany.pipe(take(1)).toPromise();
            const currentInquiryId = personaCompany.inquiry?.inquiryId;
            const currentInquiryStatus = personaCompany.inquiry?.status;

            if (!currentInquiryId) {
                return undefined;
            }

            // Fetch fields from the current inquiry
            const currentInquiryFields = await this.fetchInquiryFields(currentInquiryId);

            // Determine which inquiry fields to use for pre-filling
            let fieldsToUse = currentInquiryFields;
            let lastApprovedInquiryId: string | undefined;

            // If current inquiry is approved, use its fields directly
            if (currentInquiryStatus === 'approved') {
                fieldsToUse = currentInquiryFields;
                lastApprovedInquiryId = currentInquiryId;
            } else {
                // If current inquiry is NOT approved, try to get last_approved_inquiry fields
                lastApprovedInquiryId = currentInquiryFields.last_approved_inquiry?.value;

                if (lastApprovedInquiryId && lastApprovedInquiryId !== '') {
                    // Make a second call to fetch the last approved inquiry fields
                    fieldsToUse = await this.fetchInquiryFields(lastApprovedInquiryId);
                }

                // If no last approved inquiry is available, throw an error to avoid creating an inquiry with no prefill data
                if (!lastApprovedInquiryId) {
                    throw new ReverificationPreviousInquiryRequiredError();
                }
            }

            // Only include allowed fields and extract values
            const prefillData: KYBPrefill = {};

            for (const [fieldName, fieldData] of Object.entries(fieldsToUse)) {
                // Only include fields that are in the allowed list
                if (!this.REVERIFICATION_INCLUDED_FIELDS.includes(fieldName)) {
                    continue;
                }

                // Only include fields with actual values (not null, undefined, or empty string)
                // Persona treats null/empty as "filled but invalid" which prevents form progression
                const value = fieldData.value;
                if (value !== null && value !== '') {
                    prefillData[fieldName] = value;
                }
            }

            // Set last_approved_inquiry to the determined inquiry ID
            if (lastApprovedInquiryId && !prefillData['last_approved_inquiry']) {
                prefillData['last_approved_inquiry'] = lastApprovedInquiryId;
            }

            // Always set the initiator_employee_id to the current user
            prefillData.initiator_employee_id = this.authService.employee.id;
            return prefillData;
        } catch (error) {
            Sentry.captureException(error, {
                tags: { context: 're-verification-prefill' },
            });
            throw error;
        }
    }

    /**
     * Checks if re-verification is due based on Persona expiration date
     */
    isReVerificationDue(): boolean {
        const expiresAt = this.authService.company.personaExpiresAt;
        if (!expiresAt || isNaN(new Date(expiresAt).getTime())) {
            return false;
        }
        // Show re-verification content 30 days before expiration
        const thirtyDaysBeforeExpiry = new Date(expiresAt);
        thirtyDaysBeforeExpiry.setDate(thirtyDaysBeforeExpiry.getDate() - 30);
        return new Date() >= thirtyDaysBeforeExpiry;
    }

    /**
     * Calculates the re-verification deadline date
     * Returns the later of {personalExpiresAt + 180 days} OR April 18, 2026
     * If personalExpiresAt is not set, returns the minimum deadline (April 18, 2026)
     */
    getReVerificationDeadlineDate(): Date {
        // Minimum deadline is April 18, 2026 at noon UTC (avoids timezone display issues)
        const minimumDeadline = new Date(KYC_KYB_RE_VERIFICATION_MINIMUM_DEADLINE);

        const expiresAt = this.authService.company.personaExpiresAt;

        if (!expiresAt || isNaN(new Date(expiresAt).getTime())) {
            // If no expiry date is set or invalid, return the minimum deadline
            return minimumDeadline;
        }

        // Calculate expiry date + 180 days
        const calculatedDeadline = new Date(expiresAt);
        calculatedDeadline.setDate(calculatedDeadline.getDate() + KYC_KYB_RE_VERIFICATION_DEADLINE_DAYS);

        // Return the later date
        return new Date(Math.max(calculatedDeadline.getTime(), minimumDeadline.getTime()));
    }

    /**
     * Checks if re-verification content should be shown based on feature flag and expiration date
     */
    async shouldShowReVerificationContent(): Promise<boolean> {
        const hasFeatureFlag = await this.featureService.has(FeatureFlag.KYC_KYB_RE_VERIFICATION);
        return hasFeatureFlag && this.isReVerificationDue();
    }

    /**
     * Clear cached inquiries and clear out any data related to the Persona Client
     */
    private refreshPersona(scope: PersonaScope): void {
        if (scope === 'KYB') {
            this._personaCompany.next(undefined);
        }
        // Always refresh the Persona Account
        this._personaAccount.next(undefined);

        // Re-calculates the payroll documents step now that KYB status may have changed
        if (this.authService.company.selfServeQuickstart) {
            this.injector.get(OnboardingStatusService).refresh();
        }

        this._personaClient?.destroy();
        this._personaClient = undefined;
    }

    /**
     * Determines whether to return the Persona Account details from the BE or via a cached value in private BehaviorSubjects
     */
    private getPersonaAccount(scope: PersonaScope): Observable<PersonaAccount> {
        // Private cached values
        const scopedAccountCache = scope === 'KYB' ? this._personaCompany : this._personaAccount;

        return scopedAccountCache.pipe(
            switchMap((personaAccount) => {
                // If the account has already been retrieved from the BE just return the cached value
                if (personaAccount) {
                    return of(personaAccount);
                }

                // If there is not an existing cached value, retrieve from the BE
                return from(this.getAccountModel(scope)).pipe(
                    tap((retrievedPersonaAccount) => scopedAccountCache.next(retrievedPersonaAccount)) // Cache the return value once retrieved
                );
            })
        );
    }

    /**
     * Model functions for KYC and KYB to retrieve inquiries from the BE
     */
    private getAccountModel = (scope: PersonaScope, entityId?: string): Promise<PersonaAccount> =>
        scope === 'KYB'
            ? PersonaAccount.param('entityType', 'company').param('entityId', this.companyId).show() // Always only check the user's own company
            : PersonaAccount.param('entityType', 'account')
                  .param('entityId', entityId ?? this.accountId) // Can check the current user or an employee they have access to view
                  .show();

    /**
     * When an inquiry is in progress we have reference to the inquiry ID.
     * Sending this to the BE allows us to generate a session token which will resume the user's inquiry process when we launch the client.
     */
    private createSessionToken(inquiryId: string): Observable<ResumePersonaSessionAttributes> {
        return from(new PersonaInquiryToken({ inquiryId }).create()).pipe(
            map((personaInquiryToken) => {
                // Extract values from model getters into a plain object
                return {
                    inquiryId: personaInquiryToken.inquiryId,
                    sessionToken: personaInquiryToken.sessionToken,
                };
            })
        );
    }

    /**
     * Gets the location data used to pre-fill fields in KYB and KYC flows
     */
    private getLocationData(scope: PersonaScope): Observable<{ countryCode: string[2]; office?: Office }> {
        if (scope === 'KYC') {
            return this._countries.pipe(
                take(1),
                map((countries) => ({
                    // Converts employee's address country name to country code (Canada -> CA)
                    countryCode:
                        countries
                            .find((country) => country.name === this.authService.employee.address?.country)
                            ?.code.toUpperCase() ?? '',
                }))
            );
        }
        return combineLatest([this._primaryOffice, this._countries]).pipe(
            take(1),
            map(([office, countries]) => ({
                office,
                // Converts primary office's country name to country code (Canada -> CA)
                countryCode: countries.find((country) => country.name === office.country)?.code.toUpperCase() ?? '',
            }))
        );
    }

    /**
     * Gets data used to pre-fill fields in KYB and KYC flows
     * For KYB re-verification, prefillKYBFields may already contain merged data from previous inquiry
     */
    private getFields(
        scope: PersonaScope,
        location: { countryCode: string[2]; office?: Office },
        prefillKYBFields?: KYBPrefill
    ): KYCPrefill | KYBPrefill {
        const countryCode = location?.countryCode;

        const { company, account, employee } = this.authService;
        if (scope === 'KYC') {
            // Currently we only pre-fill country code, first name, last name and email for KYC
            return {
                selected_country_code: countryCode ?? '',
                email_address: account.email,
                name_first: account.legalFirstName,
                name_last: account.legalLastName,
            };
        }

        const office = location?.office;

        return {
            ...prefillKYBFields, // This may contain re-verification data + signing officer data
            business_name: company.name,
            business_tax_identification_number: company.craBusinessNumber,
            incorporation_year: getYear(company.incorporationDate),
            business_physical_address_street_1: office?.addressLine1 ?? '',
            business_physical_address_street_2: office?.addressLine2 ?? '',
            business_physical_address_city: office?.city ?? '',
            business_physical_address_subdivision: office?.province ?? '',
            business_physical_address_postal_code: office?.postalCode ?? '',
            business_physical_address_country_code: countryCode ?? '',
            initiator_employee_id: employee.id,
            signing_officer_api_endpoint: environment.api + PayrollResources.KYBUpdateSigningOfficer,
            organization_id: company.organizationId,
        };
    }

    /**
     * Sets the private _personaClient variable and attempts to open the iFrame for the user.
     * This handles both new sessions and resumed sessions.
     */
    private openPersonaClient(
        {
            templateId,
            referenceId,
            environmentId,
            inquiryId,
            sessionToken,
        }: Partial<NewPersonaSessionAttributes & ResumePersonaSessionAttributes>,
        scope: PersonaScope,
        prefillKYBFields?: KYBPrefill
    ): void {
        this.getLocationData(scope).subscribe(
            (locationData) =>
                (this._personaClient = new PersonaClient({
                    templateId,
                    referenceId,
                    environmentId,
                    inquiryId,
                    sessionToken,
                    fields: this.getFields(scope, locationData, prefillKYBFields),
                    onReady: this.onPersonaReady.bind(this),
                    onComplete: this.onPersonaComplete.bind(this, scope),
                    onCancel: this.onPersonaCancel.bind(this, scope),
                    onError: (args): void => this.onPersonaError(args, scope),
                }))
        );
    }

    // Callbacks for the various events that can be produced from the Persona Client

    private onPersonaReady(): void {
        this._personaClient?.open();
        this._isLoading = false;
    }

    private onPersonaComplete(scope: PersonaScope): void {
        this._isLoading = false;
        this.refreshPersona(scope);
    }

    private onPersonaCancel(scope: PersonaScope): void {
        this._isLoading = false;
        this.refreshPersona(scope);
    }

    private onPersonaError(args: InquiryError, scope: PersonaScope): void {
        Sentry.captureException(args, { tags: { persona: true } });
        this._isLoading = false;
        this.refreshPersona(scope);
    }
}
